import { showToast } from "@/shared/lib/toast";
import { removeToast } from "logsApp/submissions";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

type RemoteToast = {
  id?: string | number;
  message?: string;
  text?: string;
  description?: string;
  title?: string;
  type?: string;
  variant?: string;
  severity?: string;
};

const selectRemoteToasts = (state: any): RemoteToast[] => {
  const ui = state?.remoteUi;
  if (!ui) return [];
  const candidate = ui.toast ?? ui.toasts ?? ui.toastList ?? ui.notifications ?? ui.messages;
  return Array.isArray(candidate) ? candidate : [];
};

const normalizeType = (type?: string): "success" | "error" | "warning" => {
  const value = (type || "").toLowerCase();
  if (value.includes("error") || value.includes("danger") || value.includes("fail")) return "error";
  if (value.includes("warn")) return "warning";
  return "success";
};

const RemoteToastBridge = () => {
  const dispatch = useDispatch();
  const remoteUi = useSelector((state: any) => state?.remoteUi);
  const toasts = useSelector(selectRemoteToasts);
  const shownIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log("[RemoteToastBridge] remoteUi state:", remoteUi, "| toasts found:", toasts);
    }
  }, [remoteUi, toasts]);

  useEffect(() => {
    toasts.forEach((toast, index) => {
      const message = toast?.message ?? toast?.text ?? toast?.description ?? toast?.title;
      if (!message) return;

      const id = String(toast?.id ?? `${index}:${message}`);
      if (shownIds.current.has(id)) return;
      shownIds.current.add(id);

      const safeMessage = String(message).replace(/[&<>"']/g, (ch) => {
        switch (ch) {
          case "&":
            return "&amp;";
          case "<":
            return "&lt;";
          case ">":
            return "&gt;";
          case '"':
            return "&quot;";
          case "'":
            return "&#39;";
          default:
            return ch;
        }
      });

      showToast(safeMessage, normalizeType(toast?.type ?? toast?.variant ?? toast?.severity));

      // ToastContainer is not rendered in host, so bridge must clear forwarded toasts.
      setTimeout(() => {
        try {
          dispatch(removeToast(toast?.id ?? id));
        } catch (error) {
          if (import.meta.env.DEV) {
            console.warn("[RemoteToastBridge] failed to remove remote toast", error);
          }
        }
      }, 3000);
    });
  }, [dispatch, toasts]);

  return null;
};

export default RemoteToastBridge;
